import { useNavigate, useLocation } from 'react-router'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { getKernelLoader } from '../Services/KernelRegistry'
import { GetMenuItems } from '../Tasks/UI/Menu/GetMenuItems'

interface UseMenuNavigationOptions {
    hideNotFound?: boolean
    showWarnings?: boolean
}

interface MenuClickHandler {
    (info: { key: string }): void
}

interface UseMenuNavigationReturn {
    menuItems: any[]
    handleMenuClick: MenuClickHandler
    selectedKeys: string[]
    openKeys: string[]
    handleOpenChange: (keys: string[]) => void
}

/**
 * Рекурсивно шукає ключ у дереві меню та повертає всю цепочку ключів
 * @param items - елементи меню
 * @param targetKey - ключ, який шукаємо
 * @param parentKeys - накопичувач батьківських ключів
 * @returns масив ключів від кореня до знайденого елемента або null
 */
const findKeyPath = (items: any[], targetKey: string, parentKeys: string[] = []): string[] | null => {
    for (const item of items) {
        if (!item || typeof item !== 'object') continue

        // Якщо знайшли потрібний ключ
        if (item.key === targetKey) {
            return [...parentKeys, targetKey]
        }

        // Якщо є children, шукаємо рекурсивно
        if (item.children && Array.isArray(item.children)) {
            const result = findKeyPath(item.children, targetKey, [...parentKeys, item.key])
            if (result) {
                return result
            }
        }
    }

    return null
}

/**
 * Хук для роботи з меню та навігацією
 * @param menuConfig - конфігурація меню (аналогічно першому параметру GetMenuItems)
 * @param options - опції (аналогічно другому параметру GetMenuItems)
 * @returns об'єкт з елементами меню та обробником кліку
 */
export const useMenuNavigation = (
    menuConfig: any[],
    options: UseMenuNavigationOptions = {}
): UseMenuNavigationReturn => {
    const navigate = useNavigate()
    const location = useLocation()

    // Мемоізуємо menuConfig щоб він не змінювався при кожному рендері
    const memoizedMenuConfig = useMemo(() => menuConfig, [])
    
    // Отримуємо елементи меню - мемоізуємо, щоб уникнути нескінченних циклів
    const menuItems = useMemo(() => {
        return GetMenuItems(memoizedMenuConfig, options)
    }, [memoizedMenuConfig])

    // Об'єднуємо selectedKeys та openKeys в один стан для атомарного оновлення
    const [menuState, setMenuState] = useState<{ selectedKeys: string[]; openKeys: string[] }>({
        selectedKeys: [],
        openKeys: []
    })

    // Використовуємо ref для відстеження попереднього шляху
    const prevPathnameRef = useRef<string>('')
    
    // Зберігаємо menuItems в ref щоб уникнути зайвих викликів useEffect
    const menuItemsRef = useRef(menuItems)
    
    useEffect(() => {
        menuItemsRef.current = menuItems
    }, [menuItems])

    // Синхронізуємо selectedKeys з поточним маршрутом і
    // додаємо батьківські ключі до openKeys, не скидаючи існуючий стан
    useEffect(() => {
        // Пропускаємо, якщо pathname не змінився
        if (prevPathnameRef.current === location.pathname) {
            return
        }
        prevPathnameRef.current = location.pathname

        try {
            const routeLoader = getKernelLoader('RouteKernelLoader') || getKernelLoader('route')
            if (routeLoader) {
                const currentRoute = routeLoader.findRouteByPath(location.pathname)
                
                if (currentRoute && currentRoute.key) {
                    // Визначаємо ключ для виділення в меню
                    let menuKey = currentRoute.key
                    
                    // Для карточок ресурсів (view/edit/create) виділяємо пункт меню списку ресурсу
                    if (currentRoute.isResourceGenerated) {
                        // Перевіряємо чи це карточка ресурсу (закінчується на .view, .edit, .create)
                        const cardModeSuffix = ['.view', '.edit', '.create'].find(suffix => 
                            currentRoute.key.endsWith(suffix)
                        )
                        
                        if (cardModeSuffix) {
                            // Якщо це карточка ресурсу (Vehicle.Truck.view, Vehicle.Truck.edit, Vehicle.Truck.create)
                            // виділяємо базовий ключ ресурсу (Vehicle.Truck) - це відповідає списку (list)
                            const resourceBaseKey = currentRoute.key.slice(0, -cardModeSuffix.length)
                            
                            // Перевіряємо чи є такий ключ в меню
                            const resourceKeyPath = findKeyPath(menuItems, resourceBaseKey)
                            if (resourceKeyPath) {
                                menuKey = resourceBaseKey
                            }
                        }
                    }
                    
                    const keyPath = findKeyPath(menuItemsRef.current, menuKey)
                    
                    if (keyPath && keyPath.length > 0) {
                        const lastKey = keyPath[keyPath.length - 1]!
                        const parents = keyPath.slice(0, -1)

                        // Оновлюємо обидва стани одночасно - це гарантує один ререндер
                        setMenuState(prev => {
                            const newSelectedKeys = [lastKey]
                            const newOpenKeys = parents.length > 0 
                                ? Array.from(new Set([...prev.openKeys, ...parents]))
                                : prev.openKeys
                            
                            // Перевіряємо чи щось змінилось
                            const selectedChanged = !(prev.selectedKeys.length === 1 && prev.selectedKeys[0] === lastKey)
                            const openChanged = newOpenKeys.length !== prev.openKeys.length || 
                                                !newOpenKeys.every(k => prev.openKeys.includes(k))
                            
                            if (!selectedChanged && !openChanged) {
                                return prev
                            }
                            
                            return {
                                selectedKeys: newSelectedKeys,
                                openKeys: newOpenKeys
                            }
                        })
                    } else {
                        setMenuState(prev => {
                            if (prev.selectedKeys.length === 1 && prev.selectedKeys[0] === currentRoute.key) {
                                return prev
                            }
                            return {
                                selectedKeys: [currentRoute.key],
                                openKeys: prev.openKeys
                            }
                        })
                    }
                } else {
                    setMenuState(prev => prev.selectedKeys.length === 0 ? prev : { selectedKeys: [], openKeys: prev.openKeys })
                }
            }
        } catch (error) {
            console.error('❗ Помилка в useEffect:', error)
            // Ігноруємо помилки при визначенні selectedKeys / openKeys
        }
    }, [location.pathname]) // Видалили menuItems з залежностей, використовуємо menuItemsRef.current

    // Обробник кліку по меню - мемоізуємо для стабільності референції
    const handleMenuClick: MenuClickHandler = useCallback(({ key }) => {
        // Знаходимо роут по ключу через глобальний реєстр Kernel
        try {
            const routeLoader = getKernelLoader('RouteKernelLoader') || getKernelLoader('route')

            if (routeLoader) {
                const route = routeLoader.findRouteByKey(key)
                if (route && route.path) {
                    // Використовуємо стандартну навігацію React Router
                    navigate(route.path)
                }
            }
        } catch (error) {
            console.warn('Помилка навігації:', error)
        }
    }, [navigate])

    // Обробник відкриття / закриття підменю (керований режим openKeys) - мемоізуємо
    const handleOpenChange = useCallback((keys: string[]) => {
        setMenuState(prev => ({ ...prev, openKeys: keys }))
    }, [])

    return {
        menuItems,
        handleMenuClick,
        selectedKeys: menuState.selectedKeys,
        openKeys: menuState.openKeys,
        handleOpenChange,
    }
}
